import { doc, onSnapshot } from '@firebase/firestore';
import { useState, useEffect, useCallback } from 'react';

import { db } from '../../firebase-config';
import { UPLOAD_STATUS } from './constants.ts';
import type { FileUploadType, UploadedFile, UploadStatus } from './types.ts';

const { NOT_STARTED } = UPLOAD_STATUS;

export type UploadStatusHandler = (params: { id: string }) => void | Promise<void>;

type UseFileUploadListenerOptions = {
  /**
   * A callback to handle the status change of the file upload.
   */
  onStatusChange?: Partial<Record<UploadStatus, UploadStatusHandler>>;
  /**
   * If false, the listener will not be enabled.
   */
  enabled?: boolean;
};

export const useFileUploadListener = <TFileUploadType extends FileUploadType>(
  id: string | undefined,
  { onStatusChange = {}, enabled = true }: UseFileUploadListenerOptions,
) => {
  const [data, setData] = useState<UploadedFile<TFileUploadType> | null>(null);

  const handleStatusChange = useCallback(
    async (newStatus: UploadStatus) => {
      const callback = onStatusChange[newStatus];

      if (!id) {
        return;
      }

      await callback?.({ id });
    },

    [id, onStatusChange],
  );

  useEffect(() => {
    if (!id || !enabled) {
      return () => {};
    }

    const ref = doc(db, 'file_uploads', id);

    const unsubscribe = onSnapshot(ref, async (docSnapshot) => {
      if (!docSnapshot.exists()) {
        setData(null);

        return;
      }

      const fileData = docSnapshot.data() as UploadedFile<TFileUploadType> | undefined;
      setData(fileData ?? null);

      await handleStatusChange(fileData?.status ?? NOT_STARTED);
    });

    return () => {
      unsubscribe();
    };
  }, [id, handleStatusChange, enabled]);

  return data;
};
